import {
  type CellData,
  type RowData,
  columnFacetingFeature,
  columnFilteringFeature,
  columnOrderingFeature,
  columnPinningFeature,
  columnResizingFeature,
  columnSizingFeature,
  columnVisibilityFeature,
  createExpandedRowModel,
  createFacetedMinMaxValues,
  createFacetedRowModel,
  createFacetedUniqueValues,
  createFilteredRowModel,
  createPaginatedRowModel,
  createSortedRowModel,
  rowExpandingFeature,
  rowPaginationFeature,
  rowSelectionFeature,
  rowSortingFeature,
  tableFeatures,
  type Cell as TSCell,
  type Column as TSColumn,
  type ColumnDef as TSColumnDef,
  type Header as TSHeader,
  type ReactTable,
  type Row as TSRow
} from '@tanstack/react-table';

/**
 * One feature set for every table in the app. Adding a feature here turns
 * its APIs on everywhere (per-table `enableX` options still gate whether a
 * given listing exposes it); this keeps a single `TFeatures` type parameter
 * valid for every `Table`/`Column`/`Row`/`Cell` alias below, so shared
 * components (action bars, filters, pagination) can share one prop type
 * regardless of which listing renders them.
 */
export const appTableFeatures = tableFeatures({
  columnFilteringFeature,
  columnOrderingFeature,
  columnPinningFeature,
  columnSizingFeature,
  columnResizingFeature,
  columnVisibilityFeature,
  columnFacetingFeature,
  rowExpandingFeature,
  rowPaginationFeature,
  rowSelectionFeature,
  rowSortingFeature,
  filteredRowModel: createFilteredRowModel(),
  sortedRowModel: createSortedRowModel(),
  paginatedRowModel: createPaginatedRowModel(),
  expandedRowModel: createExpandedRowModel(),
  facetedRowModel: createFacetedRowModel(),
  facetedUniqueValues: createFacetedUniqueValues(),
  facetedMinMaxValues: createFacetedMinMaxValues()
});

export type AppFeatures = typeof appTableFeatures;

/**
 * Aliases `ReactTable` (the actual return type of the `useTable` hook), not
 * the plain `Table` from `@tanstack/table-core` — that core type only exposes
 * `table.store.state`, while every call site in this app reads `table.state`
 * (the hook's React-friendly convenience getter, populated because no call
 * site passes a `useTable` selector, so `TSelected` defaults to the full
 * `TableState<AppFeatures>`).
 */
export type Table<TData extends RowData> = ReactTable<AppFeatures, TData>;
export type Column<
  TData extends RowData,
  TValue extends CellData = CellData
> = TSColumn<AppFeatures, TData, TValue>;
export type Row<TData extends RowData> = TSRow<AppFeatures, TData>;
export type Cell<
  TData extends RowData,
  TValue extends CellData = CellData
> = TSCell<AppFeatures, TData, TValue>;
export type Header<
  TData extends RowData,
  TValue extends CellData = CellData
> = TSHeader<AppFeatures, TData, TValue>;
export type ColumnDef<
  TData extends RowData,
  TValue extends CellData = CellData
> = TSColumnDef<AppFeatures, TData, TValue>;
